import { CircularProgress, Divider, Drawer, Grid, Typography } from '@material-ui/core';
import { eventoExtraordinarioTipoOptions } from 'common/genericOptions';
import {
  InstrumentoFinanceiroApiResponse,
  PagamentoResponse,
  EventoExtraordinarioTipo,
  EmissaoStatus,
  RegistroStatus,
  TipoLiquidacaoEvento,
} from 'common/genericTypes';
import {
  calcularPuRemanescente,
  criarTravaDeEnvio,
  filtrarInstrumentosElegiveis,
  filtrarInstrumentosPorTermo,
  isInstrumentoElegivelParaEvento,
  isInstrumentoMultiCustodiante,
  isSubmitDoEventoBloqueado,
  isUsuarioInternoLaqus,
  montarPayloadDeCadastro,
  montarPayloadDeReenvio,
  ordenarPorEmissaoMaisRecente,
} from 'pages/instrumentos/gerenciar/eventoExtraordinarioRegras';
import EventoExtraordinarioAutoComplete from 'components/operacoes/eventoExtraordinarioAutoComplete';
import UploadDocs, { Attachment } from 'components/uploadDocs';
import { WarningSign } from 'components/warningSign';
import { Button, Dropdown, NumberField, SmSpace } from 'm2m.components';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';

const tipoLiquidacaoEventoOptions = [
  { value: TipoLiquidacaoEvento.Direta, description: 'Liquidação Direta' },
  { value: TipoLiquidacaoEvento.Bruta, description: 'Liquidação Bruta' },
];

interface Props {
  opened: boolean;
  onClose(): void;
  onSubmitSuccess(codigoInstrumento: string): void;
  pagamentoParaReenvio?: PagamentoResponse;
}

const EventoExtraordinarioDrawer: React.FC<Props> = (props) => {
  const { opened, onClose, onSubmitSuccess, pagamentoParaReenvio } = props;
  const dispatch = useDispatch();
  const isReenvio = !!pagamentoParaReenvio;

  const postPagamentoExtraordinario = useSelector(
    (state: RootState) => state.instrumentoApi.postPagamentoExtraordinario
  );
  const postUploadPagamentoDocumento = useSelector(
    (state: RootState) => state.instrumentoApi.postUploadPagamentoDocumento
  );
  const postSolicitarLiquidacaoPagamento = useSelector(
    (state: RootState) => state.instrumentoApi.postSolicitarLiquidacaoPagamento
  );
  const getCalcularAmortizacao = useSelector((state: RootState) => state.instrumentoApi.getCalcularAmortizacao);
  const getInstrumentosEventoExtraordinarioResume = useSelector(
    (state: RootState) => state.instrumentoApi.getInstrumentosEventoExtraordinarioResume
  );

  const grupoAcesso = useSelector((state: RootState) => state.auth.usuario.grupoAcesso);
  const usuarioLogado = { grupoAcesso };
  const isUsuarioInterno = isUsuarioInternoLaqus(usuarioLogado);

  const [codigoInstrumento, setCodigoInstrumento] = useState<string>('');
  const [instrumentos, setInstrumentos] = useState<Array<InstrumentoFinanceiroApiResponse>>([]);
  const [termoDeBusca, setTermoDeBusca] = useState<string>('');
  const [instrumento, setInstrumento] = useState<InstrumentoFinanceiroApiResponse>();
  const [instrumentoError, setInstrumentoError] = useState<string>('');
  const [buscandoInstrumento, setBuscandoInstrumento] = useState<boolean>(false);
  const [saldoRemanescente, setSaldoRemanescente] = useState<number>();
  const [eventoExtraordinarioTipo, setEventoExtraordinarioTipo] = useState<EventoExtraordinarioTipo>();
  const [tipoLiquidacaoEvento, setTipoLiquidacaoEvento] = useState<TipoLiquidacaoEvento>(TipoLiquidacaoEvento.Direta);
  const [valorPagamento, setValorPagamento] = useState<number>(0);
  const [percentualCalculado, setPercentualCalculado] = useState<number>(0);
  const [valorJuros, setValorJuros] = useState<number>(0);
  const [valorPremio, setValorPremio] = useState<number>(0);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [isMultiCustodiante, setIsMultiCustodiante] = useState<boolean>(false);
  const [enviando, setEnviando] = useState(false);

  const listaCarregadaRef = useRef(false);
  const travaDeEnvioRef = useRef(criarTravaDeEnvio());

  useEffect(() => {
    if (!opened || isReenvio || listaCarregadaRef.current) {
      return;
    }

    listaCarregadaRef.current = true;
    setBuscandoInstrumento(true);
    getInstrumentosEventoExtraordinarioResume(
      dispatch,
      {
        registroStatus: [RegistroStatus.Aprovado],
        emissaoStatus: [EmissaoStatus.Emitido, EmissaoStatus.ParcialmenteEmitido],
      },
      false
    )
      .then((result) => setInstrumentos(result ?? []))
      .catch(() => setInstrumentoError('Não foi possível carregar os instrumentos elegíveis.'))
      .finally(() => setBuscandoInstrumento(false));
  }, [opened, isReenvio, dispatch, getInstrumentosEventoExtraordinarioResume]);

  const aplicarInstrumento = (escolhido?: InstrumentoFinanceiroApiResponse) => {
    setInstrumento(escolhido);
    setCodigoInstrumento(escolhido?.codigo ?? '');
    setSaldoRemanescente(escolhido ? calcularPuRemanescente(escolhido.criteriosRemuneracao) : undefined);
    setIsMultiCustodiante(isInstrumentoMultiCustodiante(escolhido));
    setInstrumentoError('');
  };

  const buscarInstrumentoDoReenvio = async (codigo: string) => {
    setBuscandoInstrumento(true);
    try {
      const result = await getInstrumentosEventoExtraordinarioResume(dispatch, { codigo: codigo.trim() }, false);
      const encontrado = result?.[0];

      if (!encontrado) {
        aplicarInstrumento(undefined);
        setInstrumentoError('Instrumento não encontrado. Verifique o código informado.');
        return;
      }

      if (!isInstrumentoElegivelParaEvento(encontrado)) {
        aplicarInstrumento(undefined);
        setCodigoInstrumento(codigo);
        setInstrumentoError('Instrumento não elegível para eventos extraordinários.');
        return;
      }

      aplicarInstrumento(encontrado);
    } catch {
      aplicarInstrumento(undefined);
      setInstrumentoError('Instrumento não encontrado. Verifique o código informado.');
    } finally {
      setBuscandoInstrumento(false);
    }
  };

  useEffect(() => {
    if (opened && pagamentoParaReenvio) {
      const codigo = pagamentoParaReenvio.instrumentoFinanceiro.codigo;
      setCodigoInstrumento(codigo);
      setEventoExtraordinarioTipo(pagamentoParaReenvio.tipo as EventoExtraordinarioTipo);
      setTipoLiquidacaoEvento(
        pagamentoParaReenvio.liquidacaoDireta ? TipoLiquidacaoEvento.Direta : TipoLiquidacaoEvento.Bruta
      );
      buscarInstrumentoDoReenvio(codigo);
    }
  }, [opened, pagamentoParaReenvio]);

  const instrumentosElegiveis = useMemo(() => filtrarInstrumentosElegiveis(instrumentos), [instrumentos]);

  const opcoesDoCombo = useMemo(() => {
    if (isReenvio) {
      return instrumento ? [instrumento] : [];
    }

    return ordenarPorEmissaoMaisRecente(instrumentosElegiveis);
  }, [isReenvio, instrumento, instrumentosElegiveis]);

  const opcoesFiltradas = useMemo(
    () => filtrarInstrumentosPorTermo(opcoesDoCombo, termoDeBusca),
    [opcoesDoCombo, termoDeBusca]
  );

  useEffect(() => {
    if (valorPagamento > 0 && instrumento && eventoExtraordinarioTipo === EventoExtraordinarioTipo.Amortizacao) {
      const request = {
        percentual: undefined,
        valorPagamento,
        valorUnitario: instrumento.criteriosRemuneracao.valorNominalUnitarioEmissao,
        puRemanescente: calcularPuRemanescente(instrumento.criteriosRemuneracao),
      };
      getCalcularAmortizacao(dispatch, request, false)
        .then((result) => {
          setPercentualCalculado(result.percentual);
        })
        .catch(() => {});
    }
  }, [valorPagamento, instrumento, eventoExtraordinarioTipo]);

  const handleCloseDrawer = () => {
    setCodigoInstrumento('');
    setTermoDeBusca('');
    setInstrumento(undefined);
    setInstrumentoError('');
    setEventoExtraordinarioTipo(undefined);
    setTipoLiquidacaoEvento(TipoLiquidacaoEvento.Direta);
    setValorPagamento(0);
    setPercentualCalculado(0);
    setSaldoRemanescente(undefined);
    setValorJuros(0);
    setValorPremio(0);
    setAttachments([]);
    setIsMultiCustodiante(false);
    onClose();
  };

  const handleAddEventoExtraordinario = async () => {
    if (!instrumento || !eventoExtraordinarioTipo || !travaDeEnvioRef.current.tentarIniciar()) return;

    setEnviando(true);

    const isLiquidacaoDireta = tipoLiquidacaoEvento === TipoLiquidacaoEvento.Direta;

    const dadosDoPayload = {
      tipo: eventoExtraordinarioTipo,
      liquidacaoDireta: isLiquidacaoDireta,
      valorPagamento,
      percentualCalculado,
      valorJuros,
      valorPremio,
    };

    try {
      if (isReenvio && pagamentoParaReenvio) {
        await postSolicitarLiquidacaoPagamento(
          dispatch,
          pagamentoParaReenvio.id,
          montarPayloadDeReenvio(dadosDoPayload),
          attachments.map((attachment) => attachment.file).filter((file): file is File => !!file),
          true
        );

        onSubmitSuccess(codigoInstrumento);
        handleCloseDrawer();
        return;
      }

      const result = await postPagamentoExtraordinario(
        dispatch,
        instrumento.instrumentoFinanceiroGuid,
        montarPayloadDeCadastro(dadosDoPayload),
        true
      );

      if (attachments.length > 0 && result?.id) {
        for (const attachment of attachments) {
          if (attachment.file) {
            await postUploadPagamentoDocumento(dispatch, result.id, attachment.file, false);
          }
        }
      }

      onSubmitSuccess(codigoInstrumento);
      handleCloseDrawer();
    } catch {
    } finally {
      travaDeEnvioRef.current.finalizar();
      setEnviando(false);
    }
  };

  const handleAttachmentsChange = (newAttachments: Attachment[]) => {
    const updatedAttachments = [...attachments, ...newAttachments];
    setAttachments(updatedAttachments);
  };

  const handleRemoveAttachment = (id: string) => {
    const updatedAttachments = attachments.filter((a) => a.id !== id);
    setAttachments(updatedAttachments);
  };

  const isLiquidacaoDireta = tipoLiquidacaoEvento === TipoLiquidacaoEvento.Direta;
  const comprovanteObrigatorio = isLiquidacaoDireta;

  const submitDisabled = isSubmitDoEventoBloqueado(usuarioLogado, {
    temInstrumento: !!instrumento,
    tipo: eventoExtraordinarioTipo,
    valorPagamento,
    quantidadeDeComprovantes: attachments.length,
    comprovanteObrigatorio,
    isMultiCustodiante,
  });

  return (
    <Drawer
      anchor="right"
      open={opened}
      onClose={() => !enviando && handleCloseDrawer()}
      PaperProps={{ style: { padding: '40px', width: 480 } }}
    >
      <Grid container>
        <Grid item md={12}>
          <Typography variant="h3">{isReenvio ? 'Reenviar Evento Extraordinário' : 'Evento Extraordinário'}</Typography>
          <SmSpace />
          <Divider variant="fullWidth" />
          <SmSpace />

          {isReenvio && pagamentoParaReenvio?.motivoReprovacao && (
            <>
              <div
                style={{ padding: '12px', backgroundColor: '#fff3e0', borderRadius: 4, border: '1px solid #ff9800' }}
              >
                <Typography variant="body2" style={{ fontWeight: 500, color: '#e65100' }}>
                  Motivo da reprovação:
                </Typography>
                <Typography variant="body2" style={{ color: '#e65100' }}>
                  {pagamentoParaReenvio.motivoReprovacao}
                </Typography>
              </div>
              <SmSpace />
            </>
          )}

          <EventoExtraordinarioAutoComplete
            instrumentos={opcoesDoCombo}
            instrumento={instrumento ?? null}
            filteredOptions={opcoesFiltradas}
            setSearchTerm={setTermoDeBusca}
            vertical
            disabled={isReenvio || enviando}
            onChange={(escolhido) => !isReenvio && !enviando && aplicarInstrumento(escolhido ?? undefined)}
          />
          {buscandoInstrumento && (
            <div style={{ display: 'flex', alignItems: 'center', marginTop: 8 }}>
              <CircularProgress size={16} style={{ marginRight: 8 }} />
              <Typography variant="body2" color="textSecondary">
                {isReenvio ? 'Buscando instrumento...' : 'Carregando instrumentos elegíveis...'}
              </Typography>
            </div>
          )}
          {!buscandoInstrumento && instrumentoError && (
            <Typography variant="body2" style={{ color: '#f44336', marginTop: 4 }}>
              {instrumentoError}
            </Typography>
          )}
          <SmSpace />

          {!isUsuarioInterno && isMultiCustodiante && (
            <>
              <WarningSign message="Este instrumento possui múltiplos custodiantes. Apenas o Diretor da Depositária pode cadastrar eventos extraordinários neste caso." />
              <SmSpace />
            </>
          )}

          <Dropdown
            label="Tipo de Evento"
            required
            options={eventoExtraordinarioTipoOptions}
            value={eventoExtraordinarioTipo ?? ''}
            onChange={(value) =>
              !isReenvio && !enviando && setEventoExtraordinarioTipo(value as EventoExtraordinarioTipo)
            }
            disabled={isReenvio || enviando}
          />
          <SmSpace />

          <NumberField
            label={'Valor a ser resgatado'}
            value={saldoRemanescente ?? 0}
            onChange={() => {}}
            numberType="eightDecimals"
            adornment={'R$'}
            adornmentPosition={'start'}
            disabled
          />
          <SmSpace />

          {instrumento && (
            <>
              <Dropdown
                label="Tipo de Liquidação"
                required
                options={tipoLiquidacaoEventoOptions}
                value={tipoLiquidacaoEvento}
                onChange={(value) => !isReenvio && !enviando && setTipoLiquidacaoEvento(value as TipoLiquidacaoEvento)}
                disabled={isReenvio || enviando}
              />
              <SmSpace />

              {eventoExtraordinarioTipo === EventoExtraordinarioTipo.Amortizacao && (
                <>
                  <NumberField
                    label={'% Remanescente total'}
                    value={instrumento?.criteriosRemuneracao.percentualAmortizacaoRemanescente ?? 0}
                    onChange={() => {}}
                    numberType="eightDecimals"
                    adornment={'%'}
                    adornmentPosition={'start'}
                    disabled
                  />
                  <SmSpace />
                </>
              )}
              {!!eventoExtraordinarioTipo && (
                <>
                  {eventoExtraordinarioTipo === EventoExtraordinarioTipo.Amortizacao && (
                    <>
                      <NumberField
                        label={'Valor Pago (unitário)'}
                        value={valorPagamento}
                        onChange={(value) => !enviando && setValorPagamento(value ?? 0)}
                        numberType="eightDecimals"
                        adornment={'R$'}
                        adornmentPosition={'start'}
                        required
                        disabled={enviando}
                      />
                      <SmSpace />
                      <NumberField
                        label={'% Amortização (calculado)'}
                        value={percentualCalculado}
                        onChange={() => {}}
                        numberType="eightDecimals"
                        adornment={'%'}
                        adornmentPosition={'end'}
                        disabled
                      />
                      <SmSpace />
                    </>
                  )}
                  <NumberField
                    label={'Valor Juros'}
                    value={valorJuros}
                    onChange={(value) => {
                      if (!enviando) {
                        setValorJuros(value as number);
                      }
                    }}
                    numberType="eightDecimals"
                    adornment={'R$'}
                    adornmentPosition={'start'}
                    disabled={enviando}
                  />
                  <SmSpace />
                  <NumberField
                    label={'Valor Prêmio'}
                    value={valorPremio}
                    onChange={(value) => {
                      if (!enviando) {
                        setValorPremio(value as number);
                      }
                    }}
                    numberType="eightDecimals"
                    adornment={'R$'}
                    adornmentPosition={'start'}
                    disabled={enviando}
                  />
                  <SmSpace />

                  <Typography variant="body2" style={{ fontWeight: 500 }}>
                    {isLiquidacaoDireta ? 'Comprovante *' : 'Comprovante'}
                  </Typography>
                  <SmSpace />
                  <UploadDocs
                    onChange={handleAttachmentsChange}
                    onDelete={handleRemoveAttachment}
                    multipleFiles={true}
                  />
                  <SmSpace />

                  <WarningSign message="Após a aprovação do evento financeiro pela Central Depositária, a liquidação ocorrerá no dia seguinte." />
                </>
              )}
            </>
          )}
          <SmSpace />
          <Button
            onClick={handleAddEventoExtraordinario}
            variant={'contained'}
            color={'primary'}
            fullWidth
            disabled={submitDisabled || enviando}
          >
            {enviando ? 'Enviando...' : isReenvio ? 'Reenviar Solicitação' : 'Solicitar Liquidação'}
          </Button>
          <SmSpace />
          <Button onClick={handleCloseDrawer} variant={'outlined'} fullWidth disabled={enviando}>
            {'Cancelar'}
          </Button>
        </Grid>
      </Grid>
    </Drawer>
  );
};

export default EventoExtraordinarioDrawer;
